import { cn } from '@invent/wl-ui-kit-next';
import { Alert, AlertDescription, AlertTitle, Button } from '@invent/wl-ui-kit-next/ui';

import type { IFinding, ISnapshotUsage } from '../api/contracts';
import { domAddress, findingText, mergeCellFindings } from './snapshot-view';

interface IFindingsPanelProps {
  findings: IFinding[];
  title: string;
  description?: string;
  /**
   * Resolves a usage code to its usage, so a finding can name the question — and, for
   * `engine.format`, reach the constraint whose `messageCode` carries the respondent's
   * copy. The whole usage rather than its label alone: a resolver that returns only what
   * today's caller happens to need has to be widened every time the copy does.
   */
  usageOf?: (usageCode: string) => ISnapshotUsage | undefined;
  /**
   * Where a usage stands in the form, so the lines run in the form's order rather than the
   * order the findings arrived in — the first "Go to question" then leads to the first step
   * with something to fix. A line about the whole response comes first.
   */
  placeOf?: (usageCode: string) => number;
  /** The cycle's year labels, so a year row's missing cells name the year (`findingText`). */
  yearLabels?: Record<string, string>;
  onAcknowledge?: (finding: IFinding) => void;
  /**
   * Leads to a finding's question: shows the step holding it and focuses the field (the survey
   * review plan's D9). Offered on a line whose usage the form draws (`drawn`), so a line about a
   * question no step shows carries no button that would lead nowhere.
   */
  onGoTo?: (finding: IFinding) => void;
  drawn?: (usageCode: string) => boolean;
  /** Where the panel sits among its neighbours; it opens with a top margin of its own. */
  className?: string;
}

/**
 * The validation surface for findings the form itself cannot show inline —
 * response-level ones, and the report a blocked submit comes back with.
 *
 * The submit gate is the server's and its rule is exact: a **hard** finding
 * blocks wherever it is scoped; a **soft** one blocks only at `block: submit`
 * and only until acknowledged. So "Acknowledge" is offered for exactly the
 * findings an ack can clear — a synthesized finding carries no `ruleId` and is
 * unackable by construction.
 *
 * A table row's per-cell `engine.required` findings are one item, naming the row and its missing
 * cells (`mergeCellFindings`, B4-7) — never one item per cell.
 */
export function FindingsPanel({
  findings,
  title,
  description,
  usageOf,
  placeOf,
  yearLabels,
  onAcknowledge,
  onGoTo,
  drawn,
  className
}: IFindingsPanelProps) {
  if (findings.length === 0) return null;

  const hasHard = findings.some((finding) => finding.severity === 'hard');
  const shown = mergeCellFindings(findings);
  if (placeOf) {
    const place = (finding: IFinding) => (finding.usage ? placeOf(finding.usage) : -1);
    shown.sort((a, b) => place(a) - place(b));
  }

  return (
    <Alert variant={hasHard ? 'destructive' : 'warning'} className={cn('mt-6', className)}>
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>
        {description && <p className="mb-2">{description}</p>}
        <ul className="grid gap-2">
          {shown.map((finding) => {
            const ackable =
              finding.severity === 'soft' &&
              finding.block === 'submit' &&
              !!finding.ruleId &&
              !finding.acked;

            // A response-level finding belongs to the response, not to any question, so it
            // is not addressable — inventing `||` for it would be a false address. The
            // selector contract is "present ⇒ addressable": omit the attribute rather than
            // stamp a value two unrelated findings could share.
            const address = finding.usage
              ? domAddress(finding.usage, finding.instanceId, finding.instanceKey)
              : undefined;

            return (
              <li
                key={`${finding.code}-${finding.usage ?? ''}-${finding.instanceId ?? ''}-${finding.instanceKey}`}
                data-address={address}
                className="flex flex-wrap items-baseline gap-2"
              >
                <span>{findingText(finding, usageOf, 'panel', yearLabels)}</span>
                {finding.acked && <span className="text-xs">· acknowledged</span>}
                {onGoTo && finding.usage && drawn?.(finding.usage) && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => onGoTo(finding)}
                  >
                    Go to question
                  </Button>
                )}
                {ackable && onAcknowledge && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => onAcknowledge(finding)}
                  >
                    Acknowledge
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      </AlertDescription>
    </Alert>
  );
}
